/**
 * Single source for the HTML tags a `container` may publish as. `ui/types` derives
 * ContainerElement["tag"] from this, and the runtime validates against it — so the
 * editor union and the published output cannot drift apart.
 */
export const CONTAINER_TAGS = [
	"div",
	"section",
	"article",
	"aside",
	"header",
	"footer",
	"main",
	/** Breadcrumb trails and in-page navigation regions. */
	"nav",
	/** Whole-card link. Degrades to `div` when nested inside another anchor. */
	"a",
	/** List wrappers; their children should be `li` text blocks. */
	"ul",
	"ol",
	/** Native disclosure widget; first child should be a `summary` text block. */
	"details",
] as const;

export type ContainerTag = (typeof CONTAINER_TAGS)[number];

const CONTAINER_TAG_SET: ReadonlySet<string> = new Set(CONTAINER_TAGS);

/** Stored documents are untrusted input; never interpolate a raw tag into markup. */
export function resolveContainerTag(
	value: unknown,
	fallback: ContainerTag = "section"
): ContainerTag {
	return typeof value === "string" && CONTAINER_TAG_SET.has(value)
		? (value as ContainerTag)
		: fallback;
}

/** `a` navigates on click and a closed `details` hides its children — both break editing. */
const CANVAS_UNSAFE_TAGS = ["a", "details"] as const;

export type CanvasContainerTag = Exclude<
	ContainerTag,
	(typeof CANVAS_UNSAFE_TAGS)[number]
>;

const CANVAS_UNSAFE_TAG_SET: ReadonlySet<string> = new Set(CANVAS_UNSAFE_TAGS);

/**
 * The canvas used to render every container as a `div`, so page CSS naming a tag
 * (`section.hero`, `article h2`) failed to match while editing — the section lost
 * its `display:grid`/`flex` and stacked, though the published page was correct.
 */
export function resolveCanvasContainerTag(value: unknown): CanvasContainerTag {
	const tag = resolveContainerTag(value, "div");
	return CANVAS_UNSAFE_TAG_SET.has(tag) ? "div" : (tag as CanvasContainerTag);
}
